import { createAsyncThunk, createSlice, nanoid } from '@reduxjs/toolkit';
import { fetchChatResponse } from '../../services/ai/chatApi';
import {
  clearChatSessionStorage,
  getCurrentChatUserScope,
  loadActiveChatSessionId,
  loadChatSessions,
  saveActiveChatSessionId,
  saveChatSessions,
} from '../../utils/ai/chatSessionStorage';

const createMessage = (role, content, extras = {}) => ({
  id: nanoid(),
  role,
  content,
  timestamp: new Date().toISOString(),
  ...extras,
});

const welcomeMessage = createMessage(
  'assistant',
  'Hi, I am the QMetrix Assistant. Ask about sprint health, burndown, story churn, defects, release readiness, or summarize the dashboard you are viewing.',
);

const GREETING_RESPONSE = 'Hi, how can I help you ?';
const SIMPLE_GREETINGS = new Set(['hi', 'hello', 'hey', 'hi!', 'hello!', 'hey!']);

const cloneWelcomeMessage = () => ({ ...welcomeMessage });

const isSimpleGreeting = (message = '') => SIMPLE_GREETINGS.has(String(message || '').trim().toLowerCase());

const getSessionTitle = (message = '') => {
  const title = String(message || '').trim().replace(/\s+/g, ' ');
  return title.length > 48 ? `${title.slice(0, 45)}...` : title;
};

const getUserMessageText = (message = {}) =>
  String(message?.requestMessage || message?.content || message?.attachment?.name || '').trim();

const createChatSession = (messages = [cloneWelcomeMessage()], title = '') => {
  const now = new Date().toISOString();
  return {
    sessionId: nanoid(),
    title,
    createdAt: now,
    updatedAt: now,
    messages,
    loading: false,
    error: null,
  };
};

const buildInitialState = () => {
  const storedSessions = loadChatSessions().map((session) => ({
    ...session,
    loading: false,
    error: null,
  }));
  const storedActiveSessionId = loadActiveChatSessionId();
  const activeStoredSession = storedSessions.find((session) => session.sessionId === storedActiveSessionId);
  const initialSession = activeStoredSession || createChatSession();

  return {
    isOpen: false,
    messages: initialSession.messages?.length ? initialSession.messages : [cloneWelcomeMessage()],
    loading: Boolean(initialSession.loading),
    error: initialSession.error || null,
    lastUserMessage: '',
    sessions: activeStoredSession ? storedSessions : [initialSession, ...storedSessions],
    activeSessionId: initialSession.sessionId,
    isHistoryOpen: false,
    userScope: getCurrentChatUserScope(),
  };
};

const omitRuntimeFields = (session) => {
  const clean = Object.assign({}, session);
  delete clean.loading;
  delete clean.error;
  return clean;
};

const persistSessionState = (state) => {
  const cleanSessions = state.sessions.map(omitRuntimeFields);
  saveChatSessions(cleanSessions);
  saveActiveChatSessionId(state.activeSessionId);
};

const getFallbackSession = (sessions = [], excludedSessionId = '') => {
  const remainingSessions = sessions.filter((session) => session.sessionId !== excludedSessionId);
  if (!remainingSessions.length) {
    return null;
  }

  return [...remainingSessions].sort(
    (first, second) => new Date(second.updatedAt || 0) - new Date(first.updatedAt || 0),
  )[0];
};

const normalizeOutgoingMessage = (message) => {
  if (typeof message === 'string') {
    return message.trim();
  }

  if (message && typeof message === 'object') {
    return String(message.message || message.content || message.text || '').trim();
  }

  return String(message || '').trim();
};

export const sendChatMessage = createAsyncThunk(
  'aiChat/sendChatMessage',
  async ({ message, attachment, sessionId } = {}, { dispatch, getState, rejectWithValue, signal }) => {
    const trimmedMessage = normalizeOutgoingMessage(message);
    const fallbackMessage =
      attachment && !trimmedMessage ? 'Please analyze the attached screenshot.' : trimmedMessage;

    if (!fallbackMessage) {
      return rejectWithValue('Please enter a message.');
    }

    const stateBefore = getState().aiChat;
    const targetSessionId = sessionId || stateBefore.activeSessionId;

    dispatch(
      addUserMessage({
        sessionId: targetSessionId,
        content: trimmedMessage,
        requestMessage: fallbackMessage,
        attachment,
      }),
    );

    if (isSimpleGreeting(trimmedMessage)) {
      return { content: GREETING_RESPONSE, targetSessionId };
    }

    try {
      const appState = getState();
      const targetSession = appState.aiChat.sessions.find((s) => s.sessionId === targetSessionId);
      const history = targetSession?.messages || appState.aiChat.messages;
      const response = await fetchChatResponse({ message: fallbackMessage, history, appState, signal });
      return { ...response, targetSessionId };
    } catch (error) {
      if (signal?.aborted) {
        throw error;
      }

      return rejectWithValue(error?.message || 'Something went wrong.');
    }
  },
  {
    condition: ({ sessionId } = {}, { getState }) => {
      const state = getState().aiChat;
      const targetSessionId = sessionId || state.activeSessionId;
      const session = state.sessions?.find((s) => s.sessionId === targetSessionId);
      if (session?.loading) {
        return false;
      }
      return true;
    },
  },
);

const initialState = buildInitialState();

const aiChatSlice = createSlice({
  name: 'aiChat',
  initialState,
  reducers: {
    openChatbot: (state) => {
      state.isOpen = true;
    },
    closeChatbot: (state) => {
      state.isOpen = false;
    },
    toggleChatbot: (state) => {
      state.isOpen = !state.isOpen;
    },
    toggleSessionHistory: (state) => {
      state.isHistoryOpen = !state.isHistoryOpen;
    },
    closeSessionHistory: (state) => {
      state.isHistoryOpen = false;
    },
    resetChatStateForCurrentUser: () => buildInitialState(),
    clearAllChatState: () => {
      clearChatSessionStorage();
      return buildInitialState();
    },
    loadChatSession: (state, action) => {
      const nextSession = state.sessions.find((session) => session.sessionId === action.payload);

      if (!nextSession) {
        return;
      }

      state.activeSessionId = nextSession.sessionId;
      state.messages = nextSession.messages?.length ? nextSession.messages : [cloneWelcomeMessage()];
      state.lastUserMessage = getUserMessageText(
        [...state.messages].reverse().find((message) => message.role === 'user'),
      );
      state.loading = Boolean(nextSession.loading);
      state.error = nextSession.error || null;
      state.isHistoryOpen = false;
      persistSessionState(state);
    },
    startNewChatSession: (state) => {
      const session = createChatSession();
      state.sessions.unshift(session);
      state.activeSessionId = session.sessionId;
      state.messages = session.messages;
      state.loading = false;
      state.error = null;
      state.lastUserMessage = '';
      state.isHistoryOpen = false;
      persistSessionState(state);
    },
    renameChatSession: (state, action) => {
      const { sessionId, title } = action.payload || {};
      const nextTitle = String(title || '').trim();

      if (!sessionId || !nextTitle) {
        return;
      }

      const sessionIndex = state.sessions.findIndex((session) => session.sessionId === sessionId);
      if (sessionIndex === -1) {
        return;
      }

      state.sessions[sessionIndex].title = nextTitle;
      state.sessions[sessionIndex].updatedAt = new Date().toISOString();
      persistSessionState(state);
    },
    deleteChatSession: (state, action) => {
      const sessionId = action.payload;
      if (!sessionId) {
        return;
      }

      const nextSessions = state.sessions.filter((session) => session.sessionId !== sessionId);
      if (nextSessions.length === state.sessions.length) {
        return;
      }

      state.sessions = nextSessions;

      if (state.activeSessionId === sessionId) {
        const fallbackSession = getFallbackSession(nextSessions, sessionId);
        if (fallbackSession) {
          state.activeSessionId = fallbackSession.sessionId;
          state.messages = fallbackSession.messages?.length ? fallbackSession.messages : [cloneWelcomeMessage()];
          state.lastUserMessage = getUserMessageText(
            [...state.messages].reverse().find((message) => message.role === 'user'),
          );
          state.loading = Boolean(fallbackSession.loading);
          state.error = fallbackSession.error || null;
        } else {
          const freshSession = createChatSession();
          state.sessions.unshift(freshSession);
          state.activeSessionId = freshSession.sessionId;
          state.messages = freshSession.messages;
          state.lastUserMessage = '';
          state.loading = false;
          state.error = null;
        }
      }

      state.isHistoryOpen = false;
      persistSessionState(state);
    },
    addUserMessage: (state, action) => {
      const payload = action.payload;
      const targetSessionId = payload?.sessionId || state.activeSessionId;
      const message =
        payload && typeof payload === 'object' && !Array.isArray(payload)
          ? createMessage('user', payload.content || '', {
              attachment: payload.attachment || null,
              requestMessage: payload.requestMessage || payload.content || payload.attachment?.name || '',
            })
          : createMessage('user', payload);

      const targetSessionIndex = state.sessions.findIndex((session) => session.sessionId === targetSessionId);
      if (targetSessionIndex !== -1) {
        state.sessions[targetSessionIndex].messages.push(message);
        state.sessions[targetSessionIndex].updatedAt = new Date().toISOString();
        if (!state.sessions[targetSessionIndex].title) {
          state.sessions[targetSessionIndex].title = getSessionTitle(
            payload?.content || payload?.attachment?.name || payload?.requestMessage || '',
          );
        }
      }

      if (state.activeSessionId === targetSessionId) {
        state.messages = state.sessions[targetSessionIndex]
          ? state.sessions[targetSessionIndex].messages
          : [...state.messages, message];
        state.lastUserMessage = getUserMessageText(message);
      }
      persistSessionState(state);
    },
    updateChatMessage: (state, action) => {
      const { id, content } = action.payload || {};
      const activeSessionIndex = state.sessions.findIndex((session) => session.sessionId === state.activeSessionId);

      if (activeSessionIndex !== -1) {
        const msgIndex = state.sessions[activeSessionIndex].messages.findIndex((m) => m.id === id);
        if (msgIndex !== -1) {
          state.sessions[activeSessionIndex].messages[msgIndex].content = content;
          state.sessions[activeSessionIndex].messages[msgIndex].timestamp = new Date().toISOString();
          state.sessions[activeSessionIndex].updatedAt = new Date().toISOString();
        }
      }

      const messageIndex = state.messages.findIndex((message) => message.id === id);
      if (messageIndex !== -1) {
        state.messages[messageIndex].content = content;
        state.messages[messageIndex].timestamp = new Date().toISOString();
      }

      const lastUserMessage = [...state.messages].reverse().find((message) => message.role === 'user');
      state.lastUserMessage = getUserMessageText(lastUserMessage);
      persistSessionState(state);
    },
    clearChatMessages: (state) => {
      state.messages = [cloneWelcomeMessage()];
      state.error = null;
      state.lastUserMessage = '';
      const activeSessionIndex = state.sessions.findIndex((session) => session.sessionId === state.activeSessionId);
      if (activeSessionIndex !== -1) {
        state.sessions[activeSessionIndex].messages = [cloneWelcomeMessage()];
        state.sessions[activeSessionIndex].error = null;
      }
      persistSessionState(state);
    },
    clearChatError: (state) => {
      state.error = null;
      const activeSessionIndex = state.sessions.findIndex((session) => session.sessionId === state.activeSessionId);
      if (activeSessionIndex !== -1) {
        state.sessions[activeSessionIndex].error = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendChatMessage.pending, (state, action) => {
        const targetSessionId = action.meta.arg?.sessionId || state.activeSessionId;
        const targetSession = state.sessions.find((session) => session.sessionId === targetSessionId);
        if (targetSession) {
          targetSession.loading = true;
          targetSession.error = null;
        }
        if (state.activeSessionId === targetSessionId) {
          state.loading = true;
          state.error = null;
        }
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        const targetSessionId =
          action.payload?.targetSessionId || action.meta.arg?.sessionId || state.activeSessionId;
        const targetSession = state.sessions.find((session) => session.sessionId === targetSessionId);
        const assistantMessage = createMessage(
          'assistant',
          action.payload?.content || 'I am ready to help with your metrics.',
          {
            intent: action.payload?.intent,
            dataSources: action.payload?.dataSources,
          },
        );

        if (targetSession) {
          targetSession.loading = false;
          targetSession.error = null;
          targetSession.messages.push(assistantMessage);
          targetSession.updatedAt = new Date().toISOString();
        }

        if (state.activeSessionId === targetSessionId) {
          state.loading = false;
          state.error = null;
          state.messages = targetSession ? targetSession.messages : [...state.messages, assistantMessage];
        }

        persistSessionState(state);
      })
      .addCase(sendChatMessage.rejected, (state, action) => {
        const targetSessionId = action.meta.arg?.sessionId || state.activeSessionId;
        const targetSession = state.sessions.find((session) => session.sessionId === targetSessionId);
        const isAborted = Boolean(action.meta?.aborted);
        const errorMessage = isAborted ? null : (action.payload || action.error?.message || 'Something went wrong.');

        if (targetSession) {
          targetSession.loading = false;
          targetSession.error = errorMessage;
          targetSession.updatedAt = new Date().toISOString();
        }

        if (state.activeSessionId === targetSessionId) {
          state.loading = false;
          state.error = errorMessage;
        }

        persistSessionState(state);
      });
  },
});

export const {
  openChatbot,
  closeChatbot,
  toggleChatbot,
  toggleSessionHistory,
  closeSessionHistory,
  resetChatStateForCurrentUser,
  clearAllChatState,
  loadChatSession,
  startNewChatSession,
  renameChatSession,
  deleteChatSession,
  addUserMessage,
  updateChatMessage,
  clearChatMessages,
  clearChatError,
} = aiChatSlice.actions;

export default aiChatSlice.reducer;
